import { HERO } from "@/constants/testIds";
import { LOGO_URL } from "@/data/brand";
import { ShieldCheck, Truck, Recycle, Star } from "lucide-react";

export default function Hero({ onBookClick }) {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative overflow-hidden border-b-2 border-black bg-[var(--kay-bg)]"
    >
      {/* Decorative hi-vis stripe at very top */}
      <div className="hi-vis-stripe h-3 w-full" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 pb-20 pt-16 md:px-8 md:pt-24 lg:grid-cols-12 lg:gap-16">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <div
            data-testid={HERO.trustBadge}
            className="mb-6 inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest hard-shadow-sm"
          >
            <ShieldCheck size={14} className="text-[var(--kay-pink)]" />
            Upper Tier Licensed Waste Carrier
          </div>

          <h1 className="font-display text-5xl uppercase leading-[0.95] sm:text-6xl md:text-7xl">
            Waste,{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-black">cleared.</span>
              <span className="absolute inset-x-[-6px] inset-y-1 -z-0 bg-[var(--kay-pink)]" />
            </span>
            <br />
            Hassle,{" "}
            <span className="relative inline-block">
              <span className="relative z-10">gone.</span>
              <span className="absolute inset-x-[-6px] bottom-1 -z-0 h-3 bg-[var(--kay-pink)]/40" />
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-base text-neutral-700 sm:text-lg">
            Woman-founded, family-run and proudly local. We clear domestic and
            tradespeople waste across Weston-super-Mare, Bristol, Taunton and the
            wider Somerset area — cheaper than a skip, and the heavy lifting's
            on us.
          </p>

          {/* Partnership strip */}
          <div
            data-testid="hero-partnership"
            className="mt-8 inline-flex max-w-xl flex-wrap items-center gap-4 border-2 border-black bg-white px-4 py-3 hard-shadow-sm"
          >
            <div className="text-[10px] font-bold uppercase leading-tight tracking-widest text-[var(--kay-pink)]">
              Working in
              <br />
              partnership
            </div>
            <div className="h-8 w-px bg-black/20" />
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_kays-junk-removal/artifacts/jgyyhda7_nsec%20.jpeg"
                alt="North Somerset Environment"
                className="h-8 w-auto object-contain sm:h-9"
                loading="lazy"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_kays-junk-removal/artifacts/4bk7pv36_nsc.png"
                alt="North Somerset Council"
                className="h-8 w-auto object-contain sm:h-9"
                loading="lazy"
              />
            </div>
          </div>

          <p className="mt-4 max-w-xl text-sm text-neutral-600 sm:text-base">
            At <span className="font-semibold text-black">Kay's Waste Management</span>,
            we work in partnership with{" "}
            <span className="font-semibold text-black">North Somerset Environment</span>{" "}
            and dispose of all waste at council-controlled facilities so you
            can be sure your waste is disposed of responsibly.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              data-testid={HERO.ctaSecondary}
              onClick={() => scrollTo("pricing")}
              className="border-2 border-black bg-white px-6 py-3 font-display text-sm uppercase tracking-widest hard-shadow-sm hover-lift"
            >
              See Prices
            </button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5 text-[var(--kay-pink)]">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={16} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <span className="font-semibold">Loved by locals</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck size={16} />
              <span className="font-semibold">Same-week collections</span>
            </div>
            <div className="flex items-center gap-2">
              <Recycle size={16} />
              <span className="font-semibold">Responsibly disposed</span>
            </div>
          </div>
        </div>

        {/* Right: logo + accents */}
        <div className="relative lg:col-span-5">
          <div className="relative mx-auto max-w-md">
            <div className="absolute -left-4 -top-4 hidden h-28 w-28 rotate-[-8deg] items-center justify-center border-2 border-black bg-[var(--kay-pink)] text-center text-black sm:flex">
              <span className="font-display text-xs uppercase leading-tight">
                Woman
                <br />
                Founded
              </span>
            </div>

            <div className="border-2 border-black bg-black hard-shadow">
              <img
                src={LOGO_URL}
                alt="Kay's Waste Management"
                className="aspect-square w-full object-cover"
              />
            </div>

            <div className="absolute -bottom-5 -right-4 hidden rotate-[6deg] border-2 border-black bg-white px-4 py-2 hard-shadow-sm sm:block">
              <div className="font-script text-2xl text-[var(--kay-pink)]">
                cheaper than a skip
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
